/**
 * Returns the translated credit card type for Core 
 *
 * @param paymentCaptureCardType - The CC type value returned by Payment Capture
 * @returns Translated CC Type value for Core transaction
 */
 export function translatePaymentCaptureCardTypeToCore(paymentCaptureCardType: any) {
  let translatedPaymentCaptureCardType = paymentCaptureCardType;

  switch (paymentCaptureCardType) {
    case 'AX':
      translatedPaymentCaptureCardType = 'American Express';
      break;
    case 'DC':
      translatedPaymentCaptureCardType = 'Diners Club';
      break;
    case 'DI':
      translatedPaymentCaptureCardType = 'Discover';
      break;
    case 'MC':
      translatedPaymentCaptureCardType = 'MasterCard';
      break;
    case 'VI':
      translatedPaymentCaptureCardType = 'Visa';
      break;
    case 'AP':
    case 'Apple Pay':
      translatedPaymentCaptureCardType = 'Apple Pay';
      break;
    default:
      break;
  }

  return translatedPaymentCaptureCardType;
}

/**
 * Returns the translated payment type for Core 
 *
 * @param paymentCapturePaymentType - The payment type value returned by Payment Capture
 * @returns Translated payment type value for Core transaction
 */
export function translatePaymentCapturePaymentTypeToCorePaymentType(paymentCapturePaymentType: any) {
  let translatedPaymentCapturePaymentType = paymentCapturePaymentType;

  switch (paymentCapturePaymentType) {
    case 'PP':
    case 'AM':
      translatedPaymentCapturePaymentType = 'Alternate Payment';
      break;
    case 'AP':
    case 'CC':
    default:
      translatedPaymentCapturePaymentType = 'Credit Card';
      break;
  }

  return translatedPaymentCapturePaymentType;
}

/**
 * Returns the translated alternate payment type for Core 
 *
 * @param paymentCapturePaymentType - The payment type value returned by Payment Capture
 * @returns Translated alternate payment type value for Core transaction
 */
export function translatePaymentCapturePaymentTypeToCoreAlternatePaymentType(paymentCapturePaymentType: any) {
  let translatedPaymentCapturePaymentType = paymentCapturePaymentType;

  switch (paymentCapturePaymentType) {
    case 'PP':
      translatedPaymentCapturePaymentType = 'PayPal';
      break;
    case 'AM':
      translatedPaymentCapturePaymentType = 'Amazon';
      break;
    case 'CC':
    case 'AP':
    default:
      translatedPaymentCapturePaymentType = 'CreditCard';
      break;
  }

  return translatedPaymentCapturePaymentType;
}

/**
 * Returns the billing agreement id for Core alternate payment transaction 
 *
 * @param paymentCaptureResponse - The payment capture response returned by Payment Capture
 * @returns Billing Agreement Id value for Core transaction
 */
export function getBillingAgreementIdFromPaymentCaptureResponse(paymentCaptureResponse: any) {
  let billingAgreementId;

  switch (paymentCaptureResponse.pwPaymentType) {
    case 'PP':
    case 'AM':
      billingAgreementId = paymentCaptureResponse.pwResponse.DigitalWallets[0].DigitalWallet.BillingAgreementId;
      break;
    case 'AP':
    case 'CC':
    default:
      break;
  }

  return billingAgreementId;
}

/**
 * Returns the transaction id/payment gateway id) for Core payment transaction 
 *
 * @param paymentCaptureResponse - The payment capture response returned by Payment Capture
 * @returns Transaction Id value for Core transaction
 */
export function getClientTransactionIdFromPaymentCaptureResponse(paymentCaptureResponse: any) {
  let clientTransactionId;

  switch (paymentCaptureResponse.pwPaymentType) {
    case 'PP':
    case 'AM':
      clientTransactionId = paymentCaptureResponse.pwResponse.DigitalWallets[0].DigitalWallet.TransactionId;
      break;
    case 'AP':
      if (paymentCaptureResponse.pwResponse.actionCode === 'Authorize') {
        clientTransactionId = paymentCaptureResponse.pwResponse.transactionId;
      }
      break;
    case 'CC':
      if (paymentCaptureResponse.pwResponse.oneTimeAuthorization.actionCode === 'Authorize') {
        clientTransactionId = paymentCaptureResponse.pwResponse.oneTimeAuthorization.paymentGatewayId;
      }
      break;
    default:
      break;
  }

  return clientTransactionId;
}

/**
 * Returns the authorization date for Core payment transaction 
 *
 * @param paymentCaptureResponse - The payment capture response returned by Payment Capture
 * @returns authorization Date value for Core transaction
 */
export function getAuthorizationDateFromPaymentCaptureResponse(paymentCaptureResponse: any) {
  let authorizationDate;

  switch (paymentCaptureResponse.pwPaymentType) {
    case 'PP':
    case 'AM':
      authorizationDate = paymentCaptureResponse.pwResponse.DigitalWallets[0].DigitalWallet.PaymentDate;
      break;
    case 'AP':
      if (paymentCaptureResponse.pwResponse.actionCode === 'Authorize') {
        authorizationDate = paymentCaptureResponse.pwResponse.authorizationDate;
      }
      break;
    case 'CC':
      if (paymentCaptureResponse.pwResponse.oneTimeAuthorization.actionCode === 'Authorize') {
        authorizationDate = paymentCaptureResponse.pwResponse.oneTimeAuthorization.authorizationDate;
      }
      break;
    default:
      break;
  }

  return authorizationDate;
}

/**
 * Returns the authorization code for Core payment transaction 
 *
 * @param paymentCaptureResponse - The payment capture response returned by Payment Capture
 * @returns authorization Code value for Core transaction
 */
export function getAuthorizationCodeFromPaymentCaptureResponse(paymentCaptureResponse: any) {
  let authorizationCode;

  switch (paymentCaptureResponse.pwPaymentType) {
    case 'AP':
      if (paymentCaptureResponse.pwResponse.actionCode === 'Authorize') {
        authorizationCode = paymentCaptureResponse.pwResponse.authCode;
      }
      break;
    case 'CC':
      if (paymentCaptureResponse.pwResponse.oneTimeAuthorization.actionCode === 'Authorize') {
        authorizationCode = paymentCaptureResponse.pwResponse.oneTimeAuthorization.authorizationCode;
      }
      break;
    case 'PP':
    case 'AM':
    default:
      break;
  }

  return authorizationCode;
}

/**
 * Returns the presettle indicator for Core payment transaction 
 *
 * @param paymentCaptureResponse - The payment capture response returned by Payment Capture
 * @returns presettle indicator  value for Core transaction
 */
export function getPresettleIndicatorFromPaymentCaptureResponse(paymentCaptureResponse: any) {
  let presettleIndicator;

  switch (paymentCaptureResponse.pwPaymentType) {
    case 'AM':
    case 'PP':
      presettleIndicator = 'Authorized';
      break;
    case 'AP':
      if (paymentCaptureResponse.pwResponse.actionCode === 'Authorize') {
        presettleIndicator = 'Authorized';
      }
      break;
    case 'CC':
      if (paymentCaptureResponse.pwResponse.oneTimeAuthorization.actionCode === 'Authorize') {
        presettleIndicator = 'Authorized';
      }
      break;
    default:
      break;
  }

  return presettleIndicator;
}
